/**
 * Mirror config şablonları — aile bazlı template motoru.
 *
 * Yeni mirror eklendiğinde DISTRO_FAMILY tablosuna bir satır ekle, gerisini
 * template fonksiyonları halleder.
 *
 * Aile listesi:
 *   - deb     : apt sources.list (Ubuntu, Debian, Kali, Mint, vs.)
 *   - rpm     : yum/dnf .repo (CentOS, Rocky, Alma, Fedora, EPEL, vs.)
 *   - arch    : pacman mirrorlist (Arch, Manjaro, Artix, Garuda, vs.)
 *   - alpine  : apk repositories
 *   - opensuse: zypper .repo
 *   - generic : sadece base URL bilgisi
 */

const BASE_URL = 'http://mirror.rabisu.com';

/* ── Aile Eşlemesi ───────────────────────────────────────────── */
export const DISTRO_FAMILY = {
  // deb tabanlı
  ubuntu:           { family: 'deb',     components: 'main restricted universe multiverse' },
  'ubuntu-ports':   { family: 'deb',     components: 'main restricted universe multiverse' },
  debian:           { family: 'deb',     components: 'main contrib non-free' },
  kali:             { family: 'deb',     components: 'main contrib non-free non-free-firmware' },
  raspbian:         { family: 'deb',     components: 'main contrib non-free' },
  devuan:           { family: 'deb',     components: 'main contrib non-free' },
  deepin:           { family: 'deb',     components: 'main contrib non-free' },
  linuxmint:        { family: 'deb',     components: 'main upstream import backport' },
  mxlinux:          { family: 'deb',     components: 'main non-free' },

  // rpm tabanlı
  centos:           { family: 'rpm',     gpg: 'RPM-GPG-KEY-CentOS' },
  'centos-stream':  { family: 'rpm',     gpg: 'RPM-GPG-KEY-CentOS-Official' },
  rocky:            { family: 'rpm',     gpg: 'RPM-GPG-KEY-Rocky' },
  almalinux:        { family: 'rpm',     gpg: 'RPM-GPG-KEY-AlmaLinux' },
  fedora:           { family: 'rpm',     gpg: 'RPM-GPG-KEY-fedora' },
  epel:             { family: 'rpm',     gpg: 'RPM-GPG-KEY-EPEL' },

  // arch tabanlı
  archlinux:        { family: 'arch' },
  manjaro:          { family: 'arch' },
  artix:            { family: 'arch' },
  garuda:           { family: 'arch' },
  blackarch:        { family: 'arch' },

  // alpine
  alpinelinux:      { family: 'alpine' },
  alpine:           { family: 'alpine' },

  // opensuse
  opensuse:         { family: 'opensuse' },

  // jenerik (sadece base url)
  savannah:         { family: 'generic' },
  gnu:              { family: 'generic' },
  voidlinux:        { family: 'generic' },
  solus:            { family: 'generic' },
  kaos:             { family: 'generic' },
  slackware:        { family: 'generic' },
  gentoo:           { family: 'generic' },
  cran:             { family: 'generic' },
  ctan:             { family: 'generic' },
  termux:           { family: 'generic' },
  wireshark:        { family: 'generic' },
  kernel:           { family: 'generic' },
  kubernetes:       { family: 'generic' },
  libreoffice:      { family: 'generic' },
  mageia:           { family: 'generic' },
  zorin:            { family: 'generic' },
};

/* ── Yaygın sürüm önerileri (dropdown için) ──────────────────── */
export const COMMON_VERSIONS = {
  ubuntu:           ['noble', 'jammy', 'focal', 'bionic'],
  'ubuntu-ports':   ['noble', 'jammy', 'focal'],
  debian:           ['trixie', 'bookworm', 'bullseye', 'buster'],
  kali:             ['kali-rolling'],
  raspbian:         ['bookworm', 'bullseye', 'buster'],
  devuan:           ['daedalus', 'chimaera', 'beowulf'],
  deepin:           ['apricot', 'beige'],
  linuxmint:        ['wilma', 'virginia', 'victoria'],
  mxlinux:          ['bookworm', 'bullseye'],

  centos:           ['7', '8'],
  'centos-stream':  ['9', '10'],
  rocky:            ['8', '9', '10'],
  almalinux:        ['8', '9', '10'],
  fedora:           ['41', '40', '39'],
  epel:             ['7', '8', '9'],

  alpinelinux:      ['v3.20', 'v3.19', 'v3.18', 'edge'],
  alpine:           ['v3.20', 'v3.19', 'v3.18', 'edge'],

  opensuse:         ['Tumbleweed', 'Leap 15.6', 'Leap 15.5'],
};

/* ── Helper'lar ──────────────────────────────────────────────── */
function getFamily(distro) {
  return DISTRO_FAMILY[distro] || { family: 'generic' };
}

/* ── Template'ler ────────────────────────────────────────────── */

/** apt sources.list — Debian/Ubuntu/Kali ailesi */
function buildDeb(distro, version) {
  const cfg  = getFamily(distro);
  const comp = cfg.components || 'main';
  const path = `${BASE_URL}/${distro}/`;
  const lines = [
    `# Generated by Rabisu Mirror`,
    `# Save as /etc/apt/sources.list (or /etc/apt/sources.list.d/rabisu.list)`,
    ``,
    `deb ${path} ${version} ${comp}`,
    `# deb-src ${path} ${version} ${comp}`,
  ];
  // Updates / security yalnız Debian/Ubuntu için anlamlı
  if (['ubuntu', 'ubuntu-ports', 'debian'].includes(distro)) {
    lines.push(
      ``,
      `deb ${path} ${version}-updates ${comp}`,
      `deb ${path} ${version}-backports ${comp}`,
      `deb ${path} ${version}-security ${comp}`,
    );
  }
  return lines.join('\n');
}

/** yum/dnf .repo — RHEL ailesi */
function buildRpm(distro, version) {
  const cfg  = getFamily(distro);
  const gpg  = cfg.gpg || 'RPM-GPG-KEY-' + distro;
  const path = `${BASE_URL}/${distro}/${version}`;

  // Fedora ve EPEL yapısı biraz farklı
  if (distro === 'fedora') {
    return [
      `# Save as /etc/yum.repos.d/rabisu-fedora.repo`,
      `[fedora]`,
      `name=Fedora $releasever - $basearch`,
      `baseurl=${BASE_URL}/fedora/releases/$releasever/Everything/$basearch/os/`,
      `enabled=1`,
      `gpgcheck=1`,
      `gpgkey=file:///etc/pki/rpm-gpg/${gpg}-$releasever-$basearch`,
    ].join('\n');
  }

  if (distro === 'epel') {
    return [
      `# Save as /etc/yum.repos.d/rabisu-epel.repo`,
      `[epel]`,
      `name=Extra Packages for Enterprise Linux $releasever - $basearch`,
      `baseurl=${BASE_URL}/epel/$releasever/Everything/$basearch/`,
      `enabled=1`,
      `gpgcheck=1`,
      `gpgkey=file:///etc/pki/rpm-gpg/${gpg}-$releasever`,
    ].join('\n');
  }

  // CentOS / Rocky / Alma standardı: BaseOS + AppStream
  const repoName = distro === 'centos-stream' ? 'centos-stream' : distro;
  return [
    `# Save as /etc/yum.repos.d/rabisu-${repoName}.repo`,
    `[baseos]`,
    `name=${distro.toUpperCase()} ${version} - BaseOS`,
    `baseurl=${path}/BaseOS/$basearch/os/`,
    `enabled=1`,
    `gpgcheck=1`,
    `gpgkey=file:///etc/pki/rpm-gpg/${gpg}`,
    ``,
    `[appstream]`,
    `name=${distro.toUpperCase()} ${version} - AppStream`,
    `baseurl=${path}/AppStream/$basearch/os/`,
    `enabled=1`,
    `gpgcheck=1`,
    `gpgkey=file:///etc/pki/rpm-gpg/${gpg}`,
  ].join('\n');
}

/** pacman mirrorlist — Arch ailesi */
function buildArch(distro) {
  const path = distro === 'archlinux'
    ? `${BASE_URL}/archlinux/$repo/os/$arch`
    : `${BASE_URL}/${distro}/$repo/os/$arch`;
  return [
    `# Save as /etc/pacman.d/mirrorlist (top entry)`,
    `Server = ${path}`,
  ].join('\n');
}

/** apk repositories — Alpine */
function buildAlpine(distro, version) {
  const v = version.startsWith('v') ? version : 'v' + version;
  return [
    `# Save as /etc/apk/repositories`,
    `${BASE_URL}/alpine/${v}/main`,
    `${BASE_URL}/alpine/${v}/community`,
  ].join('\n');
}

/** zypper .repo — openSUSE */
function buildOpensuse(distro, version) {
  // Tumbleweed ve Leap yolları farklı
  const isTw = /tumbleweed/i.test(version);
  const path = isTw
    ? `${BASE_URL}/opensuse/tumbleweed/repo/`
    : `${BASE_URL}/opensuse/distribution/leap/${version.replace(/^Leap\s+/i, '')}/repo/`;
  return [
    `# Disable existing repos:  sudo zypper modifyrepo -ad`,
    `# Save below content as /etc/zypp/repos.d/rabisu.repo`,
    `# Refresh:  sudo zypper ref`,
    ``,
    `[rabisu-oss]`,
    `name=Rabisu OSS Repository`,
    `enabled=1`,
    `autorefresh=0`,
    `baseurl=${path}oss/`,
    `type=rpm-md`,
    ``,
    `[rabisu-non-oss]`,
    `name=Rabisu Non-OSS Repository`,
    `enabled=1`,
    `autorefresh=0`,
    `baseurl=${path}non-oss/`,
    `type=rpm-md`,
  ].join('\n');
}

/** Jenerik — sadece URL bilgisi */
function buildGeneric(distro) {
  return [
    `# ${distro.toUpperCase()} mirror`,
    `# Browse:  ${BASE_URL}/${distro}/`,
    ``,
    `# Use this base URL with your distro's package manager:`,
    `${BASE_URL}/${distro}/`,
  ].join('\n');
}

/* ── Ana Fonksiyon ───────────────────────────────────────────── */
/**
 * Verilen mirror adı ve sürüm için config metni üret.
 * @param {string} distro - mirror adı (örn. "ubuntu", "centos-stream")
 * @param {string} version - sürüm (örn. "jammy", "9", "Tumbleweed"); jenerik için boş olabilir
 * @returns {string} config metni
 */
export function generateConfig(distro, version) {
  const { family } = getFamily(distro);

  switch (family) {
    case 'deb':      return buildDeb(distro, version || 'stable');
    case 'rpm':      return buildRpm(distro, version || '$releasever');
    case 'arch':     return buildArch(distro);
    case 'alpine':   return buildAlpine(distro, version || 'edge');
    case 'opensuse': return buildOpensuse(distro, version || 'Tumbleweed');
    case 'generic':
    default:         return buildGeneric(distro);
  }
}

/** Bir mirror için sürüm gerekli mi? */
export function needsVersion(distro) {
  const { family } = getFamily(distro);
  return family === 'deb' || family === 'rpm' || family === 'alpine' || family === 'opensuse';
}
